import { useState } from 'react';
import { authedFetch } from '@/entities/user';
import { trpc } from '@/shared/api';

/** REST 응답 봉투(ApiResponse) 중 오류 표시에 필요한 부분. */
interface ErrorEnvelope {
  readonly error: { readonly message: string } | null;
}

interface UseUserModerationResult {
  /** 진행 중인 대상 사용자 id. 버튼별 로딩 표시에 쓴다. */
  readonly pendingUserId: string | null;
  readonly error: string | null;
  readonly block: (userId: string, reason: string) => Promise<boolean>;
  readonly unblock: (userId: string) => Promise<boolean>;
}

/**
 * 운영진 차단 · 해제. tRPC 라우터에 없는 REST 엔드포인트라 authedFetch 를 쓴다.
 * (POST /api/admin/users/:id/block · /unblock — 설계 Phase 8)
 */
export const useUserModeration = (): UseUserModerationResult => {
  const [pendingUserId, setPendingUserId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const utils = trpc.useUtils();

  const request = async (userId: string, path: string, body?: unknown): Promise<boolean> => {
    setPendingUserId(userId);
    setError(null);

    try {
      const response = await authedFetch(path, {
        method: 'POST',
        ...(body !== undefined
          ? { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }
          : {}),
      });

      if (!response.ok) {
        const envelope = (await response.json().catch(() => null)) as ErrorEnvelope | null;
        setError(envelope?.error?.message ?? '요청을 처리하지 못했습니다.');
        return false;
      }

      // 차단 상태가 바뀌었으므로 목록과 상세(users.adminGet)를 함께 다시 불러온다.
      await utils.users.invalidate();
      return true;
    } catch {
      setError('요청을 처리하지 못했습니다. 네트워크를 확인해 주세요.');
      return false;
    } finally {
      setPendingUserId(null);
    }
  };

  return {
    pendingUserId,
    error,
    block: (userId, reason) => request(userId, `/api/admin/users/${userId}/block`, { reason }),
    unblock: (userId) => request(userId, `/api/admin/users/${userId}/unblock`),
  };
};
